import React, { useState } from 'react';
import axios from 'axios';
import { User, updateUser } from '../../utils/getData/User';
import EmailChangeButton from '../../subComponents/mypage/EmailChangeButton';
import { validateHandler, validatePostcode, validatePrefecture, validateCity, validateApartment, validatePhoneNumber } from '../../utils/validate/Validate';

type UserInfoFormProps = {
  user: User,
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>
}

export const UserInfoForm: React.FC<UserInfoFormProps> = ({ user, setUser }) => {
  const [postcode, setPostcode] = useState(user.postcode);
  const [postcodeError, setPostcodeError] = useState("");
  const [prefecture, setPrefecture] = useState(user.prefecture);
  const [prefectureError, setPrefectureError] = useState("");
  const [city, setCity] = useState(user.city);
  const [cityError, setCityError] = useState("");
  const [apartment, setApartment] = useState(user.building ?? "");
  const [apartmentError, setApartmentError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(user.phone_number);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [postError, setPostError] = useState("");
  const [updated, setUpdated] = useState(false);
  const [sending, setSending] = useState(false);

  const handlePostcodeChange = validateHandler(setPostcode, setPostcodeError, validatePostcode);
  const handlePrefectureChange = validateHandler(setPrefecture, setPrefectureError, validatePrefecture);
  const handleCityChange = validateHandler(setCity, setCityError, validateCity);
  const handleApartmentChange = validateHandler(setApartment, setApartmentError, validateApartment);
  const handlePhoneNumberChange = validateHandler(setPhoneNumber, setPhoneNumberError, validatePhoneNumber);

  const allFieldsValid = () => {
    return !(
      postcodeError ||
      prefectureError ||
      cityError ||
      apartmentError ||
      phoneNumberError
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!allFieldsValid() || sending) {
      // 通常はここを通らないので何もしない
      return;
    }

    setSending(true);
    setPostError("");
    setUpdated(false);
    try {
      const response = await updateUser({
        postcode: postcode,
        prefecture: prefecture,
        city: city,
        building: apartment,
        phone_number: phoneNumber,
      });
      setUser(response);
      setUpdated(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        setPostError("入力内容に誤りがあります。各項目をご確認ください。");
      } else {
        setPostError("変更に失敗しました。時間を空けてもう一度お試しください。");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="contentContainer" onSubmit={handleSubmit}>
      <div className="px-2 pb-3 border-bottom">
        <p className="title">お名前</p>
        <p className="fs-description">{user.last_name} {user.first_name} 様</p>
        <p className="fs-caption mt-1">
          ※お名前の変更はこの画面では行えません。変更が必要な場合はお問い合わせください。
        </p>
      </div>
      <div className="px-2 pb-3 border-bottom">
        <p className="title">メールアドレス</p>
        <p className="fs-description text-break mb-2">{user.email}</p>
        <EmailChangeButton />
        <p className="fs-caption mt-1">
          ※変更後のメールアドレス宛に確認メールを送信し、メールに記載されたURLにアクセスすると変更が完了します。
        </p>
      </div>
      <div className="px-2 pb-3 border-bottom">
        <p className="title">郵便番号</p>
        <div className="flex-between">
          <input
            className="form-control w-40"
            type="text"
            name="postcode"
            placeholder="5460034"
            value={postcode}
            onChange={handlePostcodeChange} />
        </div>
        <div className="text-danger">{postcodeError}</div>
        <p className="fs-caption mt-1">
          ※半角数字で入力してください。
        </p>
        <p className="title">都道府県</p>
        <input
          className="form-control"
          type="text"
          name="prefecture"
          value={prefecture}
          onChange={handlePrefectureChange} />
        <div className="text-danger">{prefectureError}</div>
        <p className="title">市区町村</p>
        <input
          className="form-control"
          type="text"
          name="city"
          value={city}
          onChange={handleCityChange} />
        <div className="text-danger">{cityError}</div>
        <p className="title">マンション名<span className="badge bg-secondary ms-1">任意</span></p>
        <input
          className="form-control"
          type="text"
          name="building"
          value={apartment}
          onChange={handleApartmentChange} />
        <div className="text-danger">{apartmentError}</div>
        <p className="fs-caption mt-1">
          ※全角英数字、スペース、記号は使用できません。
        </p>
        <p className="fs-caption mt-1">
          ※住所情報や郵便番号を変更した場合であっても、すでにご依頼いただいた発送依頼のお届け先は変更されません。
        </p>
      </div>
      <div className="px-2 pb-3 border-bottom">
        <p className="title">電話番号</p>
        <input
          className="form-control"
          type="text"
          name="phone_number"
          placeholder="09012345678"
          value={phoneNumber}
          onChange={handlePhoneNumberChange} />
        <div className="text-danger">{phoneNumberError}</div>
        <p className="fs-caption mt-1">
          ※半角数字で入力してください。
        </p>
        {!allFieldsValid() && (
          <p className="text-danger text-center">修正が必要な項目があります。</p>
        )}
        {postError !== "" && (
          <p className="text-danger text-center">{postError}</p>
        )}
        {updated && (
          <p className="text-center">登録情報を変更しました。</p>
        )}
        <input
          className="btn btn-primary min-w-200 mx-auto my-4 px-2 py-1"
          type="submit"
          disabled={!allFieldsValid() || sending}
          value={sending ? "変更中..." : "変更する"} />
      </div>
    </form>
  );
};

export default UserInfoForm;
